import { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, X, Loader2, Users, Ban } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useT } from '@/i18n';
import { useBodyScrollLock } from '@/lib/useBodyScrollLock';
import { toast } from '@/stores/toast.store';
import { householdApi } from '@/features/screening/household.api';
import { ApiError } from '@/lib/api-client';

/**
 * v0.6.0 Fase 11.12 — surfaces backend 400 INCOMPLETE_HOUSEHOLD_MEMBERS
 * payload from screenings.submit. Lists pending members + 3 actions:
 *   - "Completar miembros" → onCompleteRequest closes modal + caller
 *     scrolls/focuses HM section in ScreeningPage
 *   - "Declinar todos pendientes" → loops per-HM decline endpoint with
 *     reason='other' + reasonText explaining bulk source. On success
 *     onAllDeclined fires so caller can retry submit
 *   - "Cancelar" → closes modal, no-op
 *
 * Backend pre-check (screenings.service.ts submit) prevents the
 * sub-screening huérfano bug where an in_progress sub-screening gets
 * orphaned (parent submits, sub-screening row stuck is_submitted=false,
 * uploader queue can't pick it up because of the is_submitted=TRUE
 * filter — data lost). Defense in depth: HouseholdMembersStep already
 * has the parent-Siguiente advancement guard (v0.5.2 d2227ee), this is
 * the backend + safety modal layer.
 */
export interface IncompleteHmModalProps {
  open: boolean;
  parentScreeningId: string;
  pendingMembers: Array<{ hmId: string; fullName: string; status: 'pending' | 'in_progress' }>;
  onClose: () => void;
  /** Called when admin clicks "Completar miembros". Caller closes the
   *  modal + scrolls/focuses the HouseholdMembersStep section. */
  onCompleteRequest: () => void;
  /** Called after all pending HMs are bulk-declined. Caller can retry
   *  the submit mutation. */
  onAllDeclined: () => void;
}

export function IncompleteHmModal(props: IncompleteHmModalProps): JSX.Element {
  useBodyScrollLock(props.open);
  return createPortal(
    <AnimatePresence>
      {props.open ? <Inner {...props} /> : null}
    </AnimatePresence>,
    document.body,
  );
}

function Inner({ parentScreeningId, pendingMembers, onClose, onCompleteRequest, onAllDeclined }: IncompleteHmModalProps): JSX.Element {
  const t = useT();
  const [error, setError] = useState<string | null>(null);

  // Loops the existing decline endpoint per HM (no new bulk endpoint).
  // Sequential to keep the audit log per-HM granular + idempotent if
  // partial failure (a 2nd click retries only the remaining).
  const declineMut = useMutation({
    mutationFn: async () => {
      const failures: Array<{ hmId: string; error: string }> = [];
      for (const m of pendingMembers) {
        try {
          await householdApi.decline(parentScreeningId, m.hmId, {
            reason: 'other',
            reasonText: 'Bulk decline at parent submit — incomplete household member cleanup (Fase 11.12)',
          });
        } catch (e) {
          failures.push({
            hmId: m.hmId,
            error: e instanceof ApiError ? e.message : (e as Error).message,
          });
        }
      }
      if (failures.length > 0) {
        throw new Error(`Decline failed for ${failures.length} of ${pendingMembers.length} member(s)`);
      }
      return { count: pendingMembers.length };
    },
    onSuccess: (res) => {
      toast.success(t('incompleteHm.toast.declineSuccess', { count: res.count }));
      onAllDeclined();
    },
    onError: (e) => {
      setError((e as Error).message);
    },
  });

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-8">
      {/* F5-R1a — vestido del brand: backdrop navy, hairlines, aviso amber,
          error rojo real. Estructura y acciones intactas. */}
      <motion.div
        className="absolute inset-0 bg-nav-navy/40 backdrop-blur-sm"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.div
        role="dialog"
        aria-modal="true"
        className="relative bg-white rounded-[14px] w-full max-w-md max-h-[85vh] overflow-y-auto font-body text-nav-navy"
        style={{ boxShadow: '0 24px 60px rgba(18,43,68,.14)' }}
        initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }}
      >
        <header className="sticky top-0 bg-white border-b border-nav-hair px-5 py-3 flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="w-5 h-5 mt-0.5 text-nav-amberText shrink-0" aria-hidden />
            <div>
              <h2 className="font-head text-base font-bold text-nav-navy">{t('incompleteHm.title')}</h2>
              <p className="text-xs text-nav-slate mt-0.5">{t('incompleteHm.body', { count: pendingMembers.length })}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label={t('common.close')} className="text-nav-slate hover:text-nav-navy min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-[10px] hover:bg-nav-soft">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-5 space-y-3">
          <ul className="space-y-1.5">
            {pendingMembers.map((m) => (
              <li key={m.hmId} className="flex items-center gap-2 rounded-[10px] border border-nav-card bg-nav-soft px-3 py-2">
                <Users className="w-4 h-4 text-nav-slate shrink-0" aria-hidden />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-nav-navy truncate">{m.fullName}</span>
                  <span className="block text-[11px] text-nav-slate">
                    {m.status === 'in_progress' ? t('incompleteHm.status.inProgress') : t('incompleteHm.status.pending')}
                  </span>
                </span>
              </li>
            ))}
          </ul>

          {error && (
            <div className="rounded-[10px] bg-nav-redTint border border-nav-red px-3 py-2 text-xs text-nav-red">
              {error}
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-white border-t border-nav-hair px-5 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={declineMut.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={() => declineMut.mutate()}
            disabled={declineMut.isPending}
          >
            {declineMut.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" aria-hidden />}
            <Ban className="w-3.5 h-3.5 mr-1" aria-hidden />
            {t('incompleteHm.declineAll')}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={onCompleteRequest}
            disabled={declineMut.isPending}
          >
            {t('incompleteHm.completar')}
          </Button>
        </footer>
      </motion.div>
    </div>
  );
}
